/**
 * Seeded nonparametric bootstrap with percentile, BCa, basic and normal intervals.
 *
 * Interval formulas follow Efron & Tibshirani (1993), *An Introduction to the Bootstrap*,
 * and are ports of the R `boot` package (Canty & Ripley) so that, given the same
 * replicates, `boot.ci` and this file agree to rounding error:
 * - order statistics are interpolated on the normal-quantile scale at rank (R + 1)α —
 *   `boot:::norm.inter` — so a 95% interval from 999 replicates uses ranks 25 and 975;
 * - BCa (ch. 14): z₀ = Φ⁻¹(#{θ* < θ̂} ÷ R), the acceleration
 *   a = Σ dᵢ³ ÷ (6 (Σ dᵢ²)^1.5) with dᵢ = mean(θ₍₋ⱼ₎) − θ₍₋ᵢ₎ from the jackknife (eq. 14.15),
 *   and adjusted levels α′ = Φ(z₀ + (z₀ + zα) ÷ (1 − a(z₀ + zα)));
 * - basic: 2θ̂ − the opposite percentiles; normal: θ̂ − bias ± z × bootstrap SE.
 * `boot.ci` estimates a by regression on the bootstrap output, which agrees with the
 * jackknife for smooth statistics and differs for the median.
 *
 * Random numbers come from SFC32 seeded through SplitMix32, so the same data, statistic,
 * number of resamples and seed always give the same intervals in every browser.
 */

import { normalCdf, normalQuantile } from "@/lib/stats/distributions";

export type BootStatistic = "mean" | "median" | "sd" | "correlation";

/* ------------------------------------------------------------------ random */

// biome-ignore-start lint/suspicious/noBitwiseOperators: a seeded PRNG is 32-bit integer arithmetic by definition

function splitMix32(state: number): () => number {
  let s = state >>> 0;
  return () => {
    s = (s + 0x9e_37_79_b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85_eb_ca_6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2_b2_ae_35);
    return (z ^ (z >>> 16)) >>> 0;
  };
}

/** A uniform generator on [0, 1) from an integer seed (any integer; taken mod 2³²). */
export function makeRng(seed: number): () => number {
  const next = splitMix32(Math.trunc(seed));
  let a = next();
  let b = next();
  let c = next();
  let d = 1;
  const draw = (): number => {
    const t = (((a + b) >>> 0) + d) >>> 0;
    d = (d + 1) >>> 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) >>> 0;
    c = ((c << 21) | (c >>> 11)) >>> 0;
    c = (c + t) >>> 0;
    return t / 4_294_967_296;
  };
  for (let i = 0; i < 12; i++) {
    draw();
  }
  return draw;
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the PRNG

/* --------------------------------------------------------------- statistics */

export interface StatSpec {
  n: number;
  estimate: number;
  /** The statistic of a resample given as row indices. */
  replicate: (indices: Int32Array) => number;
  /** The statistic with each row left out in turn. */
  jackknife: () => number[];
}

function sumOf(values: Iterable<number>): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total;
}

function sdFromSums(sum: number, sumSquares: number, n: number): number {
  const variance = (sumSquares - (sum * sum) / n) / (n - 1);
  return Math.sqrt(Math.max(0, variance));
}

function medianOfSorted(sortedValues: ArrayLike<number>): number {
  const n = sortedValues.length;
  const mid = Math.floor(n / 2);
  return n % 2 === 0
    ? (sortedValues[mid - 1] + sortedValues[mid]) / 2
    : sortedValues[mid];
}

function meanSpec(x: readonly number[]): StatSpec {
  const n = x.length;
  const total = sumOf(x);
  return {
    n,
    estimate: total / n,
    replicate: (idx) => {
      let s = 0;
      for (let i = 0; i < n; i++) {
        s += x[idx[i]];
      }
      return s / n;
    },
    jackknife: () => x.map((value) => (total - value) / (n - 1)),
  };
}

function sdSpec(x: readonly number[]): StatSpec {
  const n = x.length;
  const sum = sumOf(x);
  const sumSquares = sumOf(x.map((value) => value * value));
  return {
    n,
    estimate: sdFromSums(sum, sumSquares, n),
    replicate: (idx) => {
      let s = 0;
      let q = 0;
      for (let i = 0; i < n; i++) {
        const v = x[idx[i]];
        s += v;
        q += v * v;
      }
      return sdFromSums(s, q, n);
    },
    jackknife: () =>
      x.map((value) =>
        sdFromSums(sum - value, sumSquares - value * value, n - 1)
      ),
  };
}

function medianSpec(x: readonly number[]): StatSpec {
  const n = x.length;
  const sortedX = Float64Array.from(x).sort();
  const buffer = new Float64Array(n);
  return {
    n,
    estimate: medianOfSorted(sortedX),
    replicate: (idx) => {
      for (let i = 0; i < n; i++) {
        buffer[i] = x[idx[i]];
      }
      buffer.sort();
      return medianOfSorted(buffer);
    },
    jackknife: () => {
      // Median of the data with one value removed, read from the sorted array.
      const order = x.map((_, i) => i).sort((a, b) => x[a] - x[b]);
      const rank = new Int32Array(n);
      for (const [position, original] of order.entries()) {
        rank[original] = position;
      }
      const m = n - 1;
      const at = (k: number, removed: number) =>
        sortedX[k < removed ? k : k + 1];
      return x.map((_, i) => {
        const removed = rank[i];
        const mid = Math.floor(m / 2);
        return m % 2 === 0
          ? (at(mid - 1, removed) + at(mid, removed)) / 2
          : at(mid, removed);
      });
    },
  };
}

function correlationFromSums(
  n: number,
  sx: number,
  sy: number,
  sxx: number,
  syy: number,
  sxy: number
): number {
  const numerator = n * sxy - sx * sy;
  const denominator = Math.sqrt((n * sxx - sx * sx) * (n * syy - sy * sy));
  return denominator > 0 ? numerator / denominator : Number.NaN;
}

function correlationSpec(x: readonly number[], y: readonly number[]): StatSpec {
  const n = x.length;
  const sx = sumOf(x);
  const sy = sumOf(y);
  const sxx = sumOf(x.map((v) => v * v));
  const syy = sumOf(y.map((v) => v * v));
  const sxy = sumOf(x.map((v, i) => v * y[i]));
  return {
    n,
    estimate: correlationFromSums(n, sx, sy, sxx, syy, sxy),
    replicate: (idx) => {
      let a = 0;
      let b = 0;
      let aa = 0;
      let bb = 0;
      let ab = 0;
      for (let i = 0; i < n; i++) {
        const p = x[idx[i]];
        const q = y[idx[i]];
        a += p;
        b += q;
        aa += p * p;
        bb += q * q;
        ab += p * q;
      }
      return correlationFromSums(n, a, b, aa, bb, ab);
    },
    jackknife: () =>
      x.map((p, i) =>
        correlationFromSums(
          n - 1,
          sx - p,
          sy - y[i],
          sxx - p * p,
          syy - y[i] * y[i],
          sxy - p * y[i]
        )
      ),
  };
}

/** Null when the data cannot support the statistic (too few values, or no spread). */
export function makeStat(
  id: BootStatistic,
  x: readonly number[],
  y: readonly number[] = []
): StatSpec | null {
  const minimum = id === "correlation" || id === "sd" ? 3 : 2;
  if (x.length < minimum) {
    return null;
  }
  if (id === "correlation") {
    if (y.length !== x.length) {
      return null;
    }
    const spec = correlationSpec(x, y);
    return Number.isFinite(spec.estimate) ? spec : null;
  }
  const spec = { mean: meanSpec, sd: sdSpec, median: medianSpec }[id](x);
  return id === "sd" && spec.estimate === 0 ? null : spec;
}

/* ---------------------------------------------------------------- resampling */

export interface Replicates {
  /** Finite replicates, in the order drawn. */
  values: number[];
  /** Resamples whose statistic was undefined (a correlation with no spread). */
  dropped: number;
}

export function bootstrapReplicates(
  spec: StatSpec,
  resamples: number,
  seed: number
): Replicates {
  const rng = makeRng(seed);
  const { n } = spec;
  const indices = new Int32Array(n);
  const values: number[] = [];
  let dropped = 0;
  for (let b = 0; b < resamples; b++) {
    for (let i = 0; i < n; i++) {
      indices[i] = Math.floor(rng() * n);
    }
    const value = spec.replicate(indices);
    if (Number.isFinite(value)) {
      values.push(value);
    } else {
      dropped += 1;
    }
  }
  return { values, dropped };
}

/* ------------------------------------------------------------------ intervals */

/**
 * `boot:::norm.inter`: the value at rank (R + 1)α, interpolated on the normal-quantile
 * scale between the neighbouring order statistics; the extreme order statistics are used
 * when the rank falls outside 1…R.
 */
export function normInter(
  replicates: readonly number[],
  alpha: number
): number {
  const sortedT = Float64Array.from(replicates).sort();
  const r = sortedT.length;
  const rank = (r + 1) * alpha;
  const k = Math.trunc(rank);
  if (k <= 0) {
    return sortedT[0];
  }
  if (k >= r) {
    return sortedT[r - 1];
  }
  if (k === rank) {
    return sortedT[k - 1];
  }
  const lower = sortedT[k - 1];
  const upper = sortedT[k];
  const zk = normalQuantile(k / (r + 1));
  const zk1 = normalQuantile((k + 1) / (r + 1));
  return lower + ((normalQuantile(alpha) - zk) / (zk1 - zk)) * (upper - lower);
}

export interface Interval {
  lower: number;
  upper: number;
}

function tails(conf: number): [number, number] {
  return [(1 - conf) / 2, (1 + conf) / 2];
}

export function percentileInterval(
  t: readonly number[],
  conf: number
): Interval {
  const [lo, hi] = tails(conf);
  return { lower: normInter(t, lo), upper: normInter(t, hi) };
}

export function basicInterval(
  t: readonly number[],
  estimate: number,
  conf: number
): Interval {
  const [lo, hi] = tails(conf);
  return {
    lower: 2 * estimate - normInter(t, hi),
    upper: 2 * estimate - normInter(t, lo),
  };
}

function sampleVariance(t: readonly number[]): number {
  const mean = sumOf(t) / t.length;
  let total = 0;
  for (const value of t) {
    total += (value - mean) ** 2;
  }
  return total / (t.length - 1);
}

export function normalInterval(
  t: readonly number[],
  estimate: number,
  conf: number
): Interval {
  const bias = sumOf(t) / t.length - estimate;
  const margin = Math.sqrt(sampleVariance(t)) * normalQuantile((1 + conf) / 2);
  return { lower: estimate - bias - margin, upper: estimate - bias + margin };
}

/** Jackknife acceleration: a = Σd³ ÷ (6 (Σd²)^1.5), d = mean(jackknife) − jackknife. */
export function acceleration(jackknifeValues: readonly number[]): number {
  const mean = sumOf(jackknifeValues) / jackknifeValues.length;
  let cubes = 0;
  let squares = 0;
  for (const value of jackknifeValues) {
    const d = mean - value;
    cubes += d ** 3;
    squares += d * d;
  }
  return squares === 0 ? 0 : cubes / (6 * squares ** 1.5);
}

export interface BcaResult extends Interval {
  /** Bias correction z₀ = Φ⁻¹(share of replicates below the estimate). */
  z0: number;
  acceleration: number;
  /** The adjusted percentile levels actually read from the replicates. */
  adjustedLower: number;
  adjustedUpper: number;
}

/** Null when every replicate lies on one side of the estimate (z₀ infinite). */
export function bcaInterval(
  t: readonly number[],
  estimate: number,
  jackknifeValues: readonly number[],
  conf: number
): BcaResult | null {
  const below = t.filter((value) => value < estimate).length;
  const z0 = normalQuantile(below / t.length);
  if (!Number.isFinite(z0)) {
    return null;
  }
  const a = acceleration(jackknifeValues);
  const [lo, hi] = tails(conf);
  const adjust = (alpha: number) => {
    const z = z0 + normalQuantile(alpha);
    return normalCdf(z0 + z / (1 - a * z));
  };
  const adjustedLower = adjust(lo);
  const adjustedUpper = adjust(hi);
  return {
    lower: normInter(t, adjustedLower),
    upper: normInter(t, adjustedUpper),
    z0,
    acceleration: a,
    adjustedLower,
    adjustedUpper,
  };
}

/* ------------------------------------------------------------------- report */

export interface BootstrapReport {
  statistic: BootStatistic;
  n: number;
  resamples: number;
  seed: number;
  confidence: number;
  estimate: number;
  bias: number;
  standardError: number;
  replicates: number[];
  dropped: number;
  percentile: Interval;
  bca: BcaResult | null;
  basic: Interval;
  normal: Interval;
}

export function bootstrapReport(
  spec: StatSpec,
  statistic: BootStatistic,
  options: { resamples: number; seed: number; confidence: number }
): BootstrapReport {
  const { values, dropped } = bootstrapReplicates(
    spec,
    options.resamples,
    options.seed
  );
  const mean = sumOf(values) / values.length;
  return {
    statistic,
    n: spec.n,
    resamples: options.resamples,
    seed: options.seed,
    confidence: options.confidence,
    estimate: spec.estimate,
    bias: mean - spec.estimate,
    standardError: Math.sqrt(sampleVariance(values)),
    replicates: values,
    dropped,
    percentile: percentileInterval(values, options.confidence),
    bca: bcaInterval(
      values,
      spec.estimate,
      spec.jackknife(),
      options.confidence
    ),
    basic: basicInterval(values, spec.estimate, options.confidence),
    normal: normalInterval(values, spec.estimate, options.confidence),
  };
}
